import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MonthSelector, filterByMonth } from '@/components/shared/MonthSelector';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Palette, Plus, Loader2, Clock, CheckCircle2, XCircle, 
  AlertTriangle, Image, Video, FileText, Layout, MoreVertical,
  Calendar, Trash2, Zap, Link2, User, ExternalLink, MessageCircle, Send, 
  ChevronDown, ChevronUp, Grid3X3, List, ArrowRight, Copy, X, Edit2,
  Upload, Paperclip, FileIcon, Table2, Trophy
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SectionHeader } from '@/components/SectionHeader';
import { MentionInput } from '@/components/shared/MentionInput';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
  poster: { label: 'Poster', icon: Image, bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/20' },
  banner: { label: 'Banner', icon: Layout, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  video: { label: 'Video (Ads)', icon: Video, bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  video_ads: { label: 'Video (Ads)', icon: Video, bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  video_organic: { label: 'Video (Organic)', icon: Video, bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-400', border: 'border-fuchsia-500/20' },
  social_media: { label: 'Social Media', icon: FileText, bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/20' },
  other: { label: 'Other', icon: Palette, bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' }
};

// Material type label helper - shows custom name for "other" type
const getMaterialLabel = (req) => {
  if (req.request_type === 'other' && req.material_other) return req.material_other;
  return typeConfig[req.request_type]?.label || req.request_type || 'Other';
};

const statusConfig = {
  not_started: { label: 'Not Started', icon: Clock, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  pending: { label: 'Not Started', icon: Clock, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  in_progress: { label: 'In Progress', icon: AlertTriangle, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  completed: { label: 'Completed', icon: CheckCircle2, bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  amendment: { label: 'Amendment', icon: Edit2, bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
  rejected: { label: 'Rejected', icon: XCircle, bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' }
};

const urgencyConfig = {
  low: { label: 'Low', color: 'text-emerald-400', dot: 'bg-emerald-400' },
  normal: { label: 'Normal', color: 'text-amber-400', dot: 'bg-amber-400' },
  urgent: { label: 'Urgent', color: 'text-red-400', dot: 'bg-red-400', pulse: true }
};

export default function CreativeRequestPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [materialFilter, setMaterialFilter] = useState('all');
  const [channels, setChannels] = useState([]);
  const [members, setMembers] = useState([]);
  const creativeMembers = members.filter(m => (m.department || '').toLowerCase() === 'creative');
  const picMembers = creativeMembers.length > 0 ? creativeMembers : members;
  const [viewMode, setViewMode] = useState('list');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [newRequest, setNewRequest] = useState({ 
    title: '', description: '', request_type: 'poster', urgency: 'normal', 
    due_date: '', channel: '', channel_other: '', reference_links: [''], assigned_to: [],
    remarks: '',
    material_other: '',
    quantity: 1,
    sub_unit_labels: [],
  });
  const [newComments, setNewComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState(null);
  const [newCompletedLink, setNewCompletedLink] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef(null);
  const completedFileRef = React.useRef(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role_id === 'admin' || currentUser.role_id === 'manager' || currentUser.role_id === 'developer';
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  // Check if current user is assigned PIC for a request
  const isPIC = (req) => {
    if (!currentUser.id) return false;
    if (req.assigned_to === currentUser.id) return true;
    if ((req.assigned_to_list || []).includes(currentUser.id)) return true;
    return false;
  };
  // Check if user can edit (owner, PIC, or admin)
  const canEdit = (req) => req.user_id === currentUser.id || isPIC(req) || isAdmin;

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [reqRes, chRes, memRes] = await Promise.all([
        axios.get(`${API}/creative-requests`, h),
        axios.get(`${API}/active-channels`, h),
        axios.get(`${API}/team-members`, h)
      ]);
      setRequests(reqRes.data.requests || reqRes.data);
      setChannels(chRes.data.channels || []);
      setMembers(memRes.data.members || memRes.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/creative-requests/${requestId}`, { status }, h);
      setRequests(requests.map(r => r.id === requestId ? { ...r, status } : r));
      if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const handleAddRequest = async () => {
    if (!newRequest.title.trim() || !newRequest.description.trim()) { toast.error('Please fill in title and description'); return; }
    if (!newRequest.channel || (newRequest.channel === 'others' && !newRequest.channel_other?.trim())) {
      toast.error('Please pick a channel — required for scorecard tracking');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...newRequest, reference_links: newRequest.reference_links.filter(l => l.trim()) };
      if (payload.reference_links.length > 0) payload.reference_link = payload.reference_links[0];
      if (!payload.due_date) delete payload.due_date;
      if (payload.request_type !== 'other') delete payload.material_other;
      // Handle channel_other for "others" selection
      if (payload.channel === 'others' && payload.channel_other) {
        payload.channel_name = payload.channel_other;
      }
      if (!payload.channel) delete payload.channel;
      delete payload.channel_other;
      // Handle multi-PIC: send first PIC as assigned_to, full list as assigned_to_list
      const picList = (payload.assigned_to || []).filter(id => id);
      payload.assigned_to = picList[0] || '';
      payload.assigned_to_list = picList;
      if (!payload.assigned_to) delete payload.assigned_to;
      // Quantity (default 1) — backend auto-creates sub_units when > 1
      payload.quantity = Math.max(1, parseInt(payload.quantity || 1, 10));
      // Pass optional sub-unit labels through to the backend
      if (payload.quantity > 1 && Array.isArray(newRequest.sub_unit_labels) && newRequest.sub_unit_labels.length > 0) {
        payload.sub_units = Array.from({ length: payload.quantity }).map((_, i) => ({
          idx: i + 1,
          label: (newRequest.sub_unit_labels[i] || '').trim(),
        }));
      }
      delete payload.sub_unit_labels;
      const response = await axios.post(`${API}/creative-requests`, payload, h);
      setRequests([response.data.request, ...requests]);
      setShowAddModal(false);
      setNewRequest({ title: '', description: '', request_type: 'poster', urgency: 'normal', due_date: '', channel: '', channel_other: '', reference_links: [''], assigned_to: [], material_other: '', remarks: '', quantity: 1, sub_unit_labels: [] });
      toast.success('Request submitted!');
    } catch { toast.error('Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (requestId) => {
    try {
      await axios.delete(`${API}/creative-requests/${requestId}`, h);
      setRequests(requests.filter(r => r.id !== requestId));
      if (selectedRequest?.id === requestId) setSelectedRequest(null);
      toast.success('Request deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDuplicate = (req) => {
    setNewRequest({
      title: `${req.title} (Copy)`,
      description: req.description || '',
      request_type: req.request_type || 'poster',
      urgency: req.urgency || 'normal',
      due_date: '',
      channel: req.channel || '',
      channel_other: '',
      reference_links: req.reference_links?.length > 0 ? [...req.reference_links] : [''],
      assigned_to: req.assigned_to_list?.length > 0 ? [...req.assigned_to_list] : (req.assigned_to ? [req.assigned_to] : []),
      material_other: req.material_other || '',
      remarks: req.remarks || '',
      quantity: req.quantity || 1,
    });
    setShowAddModal(true);
    toast.info('Request duplicated — edit and submit');
  };

  // Per sub-unit Done / re-assign. Only PIC of that unit, owner or admin can mark done.
  const updateSubUnit = async (requestId, idx, payload) => {
    try {
      const res = await axios.put(`${API}/creative-requests/${requestId}/sub-units/${idx}`, payload, h);
      // Refresh that request from server
      const fresh = await axios.get(`${API}/creative-requests`, h);
      const list = fresh.data.requests || fresh.data;
      setRequests(list);
      if (selectedRequest?.id === requestId) {
        const updated = list.find(r => r.id === requestId);
        if (updated) setSelectedRequest(updated);
      }
      if (payload.status === 'completed') toast.success(`Unit #${idx} marked Done — credit recorded`);
      else toast.success(`Unit #${idx} updated`);
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update unit';
      toast.error(msg);
    }
  };

  const openEditModal = (req) => {
    setEditRequest({
      id: req.id,
      title: req.title,
      description: req.description,
      request_type: req.request_type || 'poster',
      material_other: req.material_other || '',
      urgency: req.urgency || 'normal',
      due_date: req.due_date || '',
      channel: req.channel || '',
      assigned_to: req.assigned_to || '',
      assigned_to_list: req.assigned_to_list?.length > 0 ? req.assigned_to_list : (req.assigned_to ? [req.assigned_to] : []),
      reference_links: (req.reference_links?.length > 0 ? req.reference_links : req.reference_link ? [req.reference_link] : ['']),
      remarks: req.remarks || '',
      quantity: req.quantity || 1,
    });
    setShowEditModal(true);
    setSelectedRequest(null);
  };

  const handleEditRequest = async () => {
    if (!editRequest?.title?.trim() || !editRequest?.description?.trim()) { toast.error('Please fill in title and description'); return; }
    setEditSubmitting(true);
    try {
      const picList = (editRequest.assigned_to_list || []).filter(id => id);
      const payload = {
        title: editRequest.title,
        description: editRequest.description,
        request_type: editRequest.request_type,
        material_other: editRequest.request_type === 'other' ? (editRequest.material_other || '') : '',
        urgency: editRequest.urgency,
        due_date: editRequest.due_date || null,
        channel: editRequest.channel || null,
        assigned_to: picList[0] || null,
        assigned_to_list: picList,
        reference_links: editRequest.reference_links.filter(l => l.trim()),
        remarks: editRequest.remarks || '',
      };
      await axios.put(`${API}/creative-requests/${editRequest.id}`, payload, h);
      setRequests(requests.map(r => r.id === editRequest.id ? { ...r, ...payload } : r));
      setShowEditModal(false);
      setEditRequest(null);
      toast.success('Request updated!');
    } catch { toast.error('Failed to update request'); }
    finally { setEditSubmitting(false); }
  };

  const handleAddComment = async (requestId) => {
    const text = newComments[requestId]?.trim();
    if (!text) return;
    setSubmittingComment(requestId);
    try {
      const res = await axios.post(`${API}/creative-requests/${requestId}/comments`, { text }, h);
      const updater = r => r.id === requestId ? { ...r, comments: [...(r.comments || []), res.data.comment] } : r;
      setRequests(requests.map(updater));
      if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, comments: [...(prev.comments || []), res.data.comment] }));
      setNewComments({ ...newComments, [requestId]: '' });
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmittingComment(null); }
  };

  const handleDeleteComment = async (requestId, commentId) => {
    try {
      await axios.delete(`${API}/creative-requests/${requestId}/comments/${commentId}`, h);
      const updater = r => r.id === requestId ? { ...r, comments: (r.comments || []).filter(c => c.id !== commentId) } : r;
      setRequests(requests.map(updater));
      if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, comments: (prev.comments || []).filter(c => c.id !== commentId) }));
    } catch { toast.error('Failed to delete comment'); }
  };

  const handleAddCompletedLink = async (requestId) => {
    if (!newCompletedLink.trim()) return;
    const req = requests.find(r => r.id === requestId);
    const updatedLinks = [...(req?.completed_links || []), newCompletedLink.trim()];
    try {
      await axios.put(`${API}/creative-requests/${requestId}`, { completed_links: updatedLinks }, h);
      const updater = r => r.id === requestId ? { ...r, completed_links: updatedLinks } : r;
      setRequests(requests.map(updater));
      if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, completed_links: updatedLinks }));
      setNewCompletedLink('');
      toast.success('Link added');
    } catch { toast.error('Failed to add link'); }
  };

  const handleRemoveCompletedLink = async (requestId, index) => {
    const req = requests.find(r => r.id === requestId);
    const updatedLinks = (req?.completed_links || []).filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/creative-requests/${requestId}`, { completed_links: updatedLinks }, h);
      const updater = r => r.id === requestId ? { ...r, completed_links: updatedLinks } : r;
      setRequests(requests.map(updater));
      if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, completed_links: updatedLinks }));
    } catch { toast.error('Failed to remove link'); }
  };

  const handleFileUpload = async (file, onSuccess) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File size must be less than 5MB'); return; }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      if (res.data.url) { onSuccess(res.data.url); toast.success('File uploaded!'); }
    } catch { toast.error('Failed to upload file'); }
    finally { setUploadingFile(false); }
  };

  const handleCompletedFileUpload = async (requestId, file) => {
    handleFileUpload(file, async (url) => {
      const req = requests.find(r => r.id === requestId);
      const updatedLinks = [...(req?.completed_links || []), url];
      try {
        await axios.put(`${API}/creative-requests/${requestId}`, { completed_links: updatedLinks }, h);
        const updater = r => r.id === requestId ? { ...r, completed_links: updatedLinks } : r;
        setRequests(requests.map(updater));
        if (selectedRequest?.id === requestId) setSelectedRequest(prev => ({ ...prev, completed_links: updatedLinks }));
      } catch { toast.error('Failed to save link'); }
    });
  };

  const monthFiltered = filterByMonth(requests, selYear, selMonth, 'created_at');
  // Carry forward: incomplete requests from previous months
  const carryForward = selMonth !== null ? requests.filter(r => {
    if (['completed', 'rejected'].includes(r.status)) return false;
    const d = new Date(r.created_at);
    return (d.getFullYear() === selYear && d.getMonth() < selMonth) || d.getFullYear() < selYear;
  }) : [];
  const visibleRequests = [...new Map([...monthFiltered, ...carryForward].map(r => [r.id, r])).values()];
  const filteredRequests = visibleRequests.filter(r => {
    let statusMatch = filter === 'all';
    if (!statusMatch) {
      if (filter === 'not_started') {
        statusMatch = r.status === 'not_started' || r.status === 'pending';
      } else {
        statusMatch = r.status === filter;
      }
    }
    const channelMatch = channelFilter === 'all' || r.channel === channelFilter;
    let materialMatch = materialFilter === 'all';
    if (!materialMatch) {
      if (materialFilter === 'video') {
        materialMatch = ['video', 'video_ads', 'video_organic'].includes(r.request_type);
      } else if (materialFilter === 'other') {
        materialMatch = !['poster', 'video', 'video_ads', 'video_organic', 'banner', 'social_media'].includes(r.request_type);
      } else {
        materialMatch = r.request_type === materialFilter;
      }
    }
    return statusMatch && channelMatch && materialMatch;
  });
  const stats = {
    total: visibleRequests.length,
    not_started: visibleRequests.filter(r => r.status === 'not_started' || r.status === 'pending').length,
    in_progress: visibleRequests.filter(r => r.status === 'in_progress').length,
    completed: visibleRequests.filter(r => r.status === 'completed').length
  };

  const getMemberName = (id) => members.find(m => m.id === id)?.name || id || '-';
  const getChannelName = (id) => {
    if (id === 'sembang-ohh-yeahh') return 'Sembang Ohh Yeahh';
    if (id === 'tenderflix') return 'Tenderflix';
    if (id === 'lotifys') return 'Lotifys';
    if (id === 'others') return 'Others';
    return channels.find(c => c.id === id)?.name || id || '-';
  };

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  const RequestCard = ({ req, isGrid }) => {
    const type = typeConfig[req.request_type] || typeConfig.other;
    const status = statusConfig[req.status] || statusConfig.pending;
    const urgency = urgencyConfig[req.urgency] || urgencyConfig.normal;
    const TypeIcon = type.icon;
    const StatusIcon = status.icon;

    return (
      <div
        data-testid={`creative-request-${req.id}`}
        className={`bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-pink-500/20 transition-all cursor-pointer group ${isGrid ? 'flex flex-col' : ''}`}
        onClick={() => setSelectedRequest(req)}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${type.bg} ${type.text} border ${type.border} text-xs`}><TypeIcon size={10} className="mr-1" />{type.label}</Badge>
            <Badge className={`${status.bg} ${status.text} border ${status.border} text-xs`}><StatusIcon size={10} className="mr-1" />{status.label}</Badge>
            <span className={`text-xs font-semibold flex items-center gap-1.5 ${urgency.color}`}>
              {urgency.pulse && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span></span>}
              {!urgency.pulse && <span className={`w-1.5 h-1.5 rounded-full ${urgency.dot}`} />}
              {urgency.label}
            </span>
          </div>
          {canEdit(req) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}><MoreVertical size={14} /></Button></DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditModal(req); }}><Edit2 size={14} className="mr-2 text-amber-400" /> Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(req); }} data-testid={`duplicate-req-${req.id}`}><Copy size={14} className="mr-2 text-cyan-400" /> Duplicate</DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUpdateStatus(req.id, 'in_progress'); }}><Clock size={14} className="mr-2 text-blue-400" /> In Progress</DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUpdateStatus(req.id, 'completed'); }}><CheckCircle2 size={14} className="mr-2 text-emerald-400" /> Completed</DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUpdateStatus(req.id, 'amendment'); }}><Edit2 size={14} className="mr-2 text-orange-400" /> Amendment</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUpdateStatus(req.id, 'rejected'); }}><XCircle size={14} className="mr-2 text-red-400" /> Rejected</DropdownMenuItem>}
                {(req.user_id === currentUser.id || isAdmin) && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this request?')) handleDelete(req.id); }} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <h3 className="font-semibold mb-1.5 group-hover:text-pink-400 transition-colors">{req.title}</h3>
        <p className="text-sm text-[hsl(var(--text-muted))] mb-3 line-clamp-2">{req.description}</p>
        
        {/* From & PIC */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[hsl(var(--text-secondary))] mb-3">
          <span className="flex items-center gap-1"><User size={11} className="text-pink-400" /> <span className="text-[hsl(var(--text-muted))]">From:</span> <span className="font-medium text-[hsl(var(--text-primary))]">{req.user_name}</span></span>
          {req.assigned_to && <span className="flex items-center gap-1"><ArrowRight size={11} className="text-blue-400" /> <span className="text-[hsl(var(--text-muted))]">PIC:</span> <span className="font-medium text-blue-400">{(req.assigned_to_list || [req.assigned_to]).filter(Boolean).map(id => getMemberName(id)).join(', ')}</span></span>}
          {req.channel && <span className="flex items-center gap-1"><Layout size={11} /> {getChannelName(req.channel)}</span>}
        </div>

        <div className="flex items-center justify-between text-xs text-[hsl(var(--text-muted))] pt-3 border-t border-[hsl(var(--border))]">
          <div className="flex items-center gap-2">
            <MessageCircle size={12} /> {(req.comments || []).length}
            {(req.completed_links || []).length > 0 && <><Link2 size={12} className="text-emerald-400" /> {(req.completed_links || []).length}</>}
          </div>
          <div className="flex items-center gap-3">
            {req.due_date && <span className="flex items-center gap-1"><Calendar size={12} /> {req.due_date}</span>}
            <span>{new Date(req.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={Palette}
          title="Creative Request"
          subtitle="Order materials from creative team"
          iconBg="bg-gradient-to-br from-pink-500 to-rose-600 shadow-pink-500/20"
          action={
            <div className="flex items-center gap-2">
              <a href="/team/creative-performance" data-testid="open-creative-performance">
                <Button variant="outline" className="border-[hsl(var(--border))]"><Trophy size={16} className="mr-2 text-amber-400" /> Performance</Button>
              </a>
              <Button data-testid="add-creative-request-btn" onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg shadow-pink-500/20"><Plus size={18} className="mr-2" /> New Request</Button>
            </div>
          }
        />

        {/* Month Selector */}
        <div className="mb-4">
          <MonthSelector year={selYear} month={selMonth} onChange={(y, m) => { setSelYear(y); setSelMonth(m); }} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'All', value: stats.total, accent: 'border-[hsl(var(--border))]', vc: '' },
            { label: 'Not Started', value: stats.not_started, accent: 'border-amber-500/30', vc: 'text-amber-400' },
            { label: 'In Progress', value: stats.in_progress, accent: 'border-blue-500/30', vc: 'text-blue-400' },
            { label: 'Completed', value: stats.completed, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
          ].map((stat) => (
            <div key={stat.label} className={`bg-[hsl(var(--card))] border ${stat.accent} rounded-xl p-4`}>
              <p className={`text-xs ${stat.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.vc}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter Row */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
            {['all', 'not_started', 'in_progress', 'completed', 'amendment', 'rejected'].map((f) => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white border-0' : 'border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]'} data-testid={`filter-${f}`}>
                {f === 'all' ? 'All Status' : statusConfig[f]?.label || f}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Materials Filter - Button Tabs */}
            <div className="flex bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-0.5 gap-0.5">
              {[
                { value: 'all', label: 'All' },
                { value: 'poster', label: 'Poster' },
                { value: 'video', label: 'Video' },
                { value: 'other', label: 'Other' },
              ].map(tab => (
                <button key={tab.value} onClick={() => setMaterialFilter(tab.value)} 
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${materialFilter === tab.value ? 'bg-pink-500/20 text-pink-400' : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-secondary))]'}`}
                  data-testid={`material-tab-${tab.value}`}
                >{tab.label}</button>
              ))}
            </div>
            {channels.length > 0 && (
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-40 h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="channel-filter"><SelectValue placeholder="All Channels" /></SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="all">All Channels</SelectItem>
                  {channels.map((ch) => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                  <SelectItem value="sembang-ohh-yeahh">Sembang Ohh Yeahh</SelectItem>
                  <SelectItem value="tenderflix">Tenderflix</SelectItem>
                  <SelectItem value="lotifys">Lotifys</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="flex bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-0.5">
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-pink-500/20 text-pink-400' : 'text-[hsl(var(--text-muted))]'}`} data-testid="view-list" title="List"><List size={16} /></button>
              <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-pink-500/20 text-pink-400' : 'text-[hsl(var(--text-muted))]'}`} data-testid="view-grid" title="Grid"><Grid3X3 size={16} /></button>
              <button onClick={() => setViewMode('classic')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'classic' ? 'bg-pink-500/20 text-pink-400' : 'text-[hsl(var(--text-muted))]'}`} data-testid="view-classic" title="Classic"><Table2 size={16} /></button>
            </div>
          </div>
        </div>

        {/* Request Cards / Classic Table */}
        {filteredRequests.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
            <Palette size={48} className="mx-auto text-pink-500/30 mb-4" />
            <p className="text-[hsl(var(--text-secondary))] font-medium">No requests found</p>
          </div>
        ) : viewMode === 'classic' ? (
          /* Classic View - Monday.com style table */
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid="classic-view">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                    <th className="text-left py-3 px-4 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[200px] sticky left-0 bg-[hsl(var(--surface))] z-10">Title</th>
                    <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[200px]">Description</th>
                    <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[120px]">Channel</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[100px]">Materials</th>
                    <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[120px]">Created By</th>
                    <th className="text-left py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[120px]">PIC</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[110px]">Status</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[100px]">Created</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[100px]">Due Date</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[90px]">Urgency</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[80px]">Ref</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[80px]">Files</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[80px]">Comments</th>
                    <th className="text-center py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider min-w-[50px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((req) => {
                    const type = typeConfig[req.request_type] || typeConfig.other;
                    const status = statusConfig[req.status] || statusConfig.pending;
                    const urgency = urgencyConfig[req.urgency] || urgencyConfig.normal;
                    const refLinks = (req.reference_links?.length > 0 ? req.reference_links : req.reference_link ? [req.reference_link] : []).filter(Boolean);
                    const completedFiles = req.completed_links || [];
                    
                    return (
                      <tr key={req.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]/50 transition-colors cursor-pointer group" onClick={() => setSelectedRequest(req)} data-testid={`classic-row-${req.id}`}>
                        <td className="py-3 px-4 sticky left-0 bg-[hsl(var(--card))] group-hover:bg-[hsl(var(--surface))]/50 z-10">
                          <span className="font-medium text-[hsl(var(--text-primary))] group-hover:text-pink-400 transition-colors">{req.title}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-[hsl(var(--text-muted))] line-clamp-2 text-xs">{req.description}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-xs text-[hsl(var(--text-secondary))]">{getChannelName(req.channel)}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Badge className={`${type.bg} ${type.text} border ${type.border} text-[10px]`}>{getMaterialLabel(req)}</Badge>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-xs text-[hsl(var(--text-secondary))]">{req.user_name || '-'}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-xs text-blue-400">{(req.assigned_to_list || [req.assigned_to]).filter(Boolean).map(id => getMemberName(id)).join(', ') || '-'}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold ${status.bg} ${status.text} border ${status.border}`}>{status.label}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="text-xs text-[hsl(var(--text-muted))]">{new Date(req.created_at).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' })}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="text-xs text-[hsl(var(--text-muted))]">{req.due_date ? new Date(req.due_date).toLocaleDateString('en-MY', { day: '2-digit', month: 'short' }) : '-'}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`text-xs font-semibold flex items-center justify-center gap-1 ${urgency.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${urgency.dot}`} />
                            {urgency.label}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {refLinks.length > 0 ? <span className="text-xs text-cyan-400 font-medium">{refLinks.length}</span> : <span className="text-xs text-[hsl(var(--text-muted))]">-</span>}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {completedFiles.length > 0 ? <span className="text-xs text-emerald-400 font-medium">{completedFiles.length}</span> : <span className="text-xs text-[hsl(var(--text-muted))]">-</span>}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {(req.comments || []).length > 0 ? (
                            <div className="flex items-center justify-center gap-1">
                              <MessageCircle size={12} className="text-pink-400" />
                              <span className="text-xs text-pink-400 font-medium">{(req.comments || []).length}</span>
                            </div>
                          ) : <span className="text-xs text-[hsl(var(--text-muted))]">0</span>}
                        </td>
                        <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                              <DropdownMenuItem onClick={() => openEditModal(req)}><Edit2 size={14} className="mr-2 text-amber-400" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(req)}><Copy size={14} className="mr-2 text-cyan-400" /> Duplicate</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(req.id, 'in_progress')}><Clock size={14} className="mr-2 text-blue-400" /> In Progress</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(req.id, 'completed')}><CheckCircle2 size={14} className="mr-2 text-emerald-400" /> Completed</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(req.id, 'amendment')}><Edit2 size={14} className="mr-2 text-orange-400" /> Amendment</DropdownMenuItem>
                              {isAdmin && <DropdownMenuItem onClick={() => handleUpdateStatus(req.id, 'rejected')}><XCircle size={14} className="mr-2 text-red-400" /> Rejected</DropdownMenuItem>}
                              {(req.user_id === currentUser.id || isAdmin) && <DropdownMenuItem onClick={() => { if (window.confirm('Delete?')) handleDelete(req.id); }} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-3'}>
            {filteredRequests.map((req) => <RequestCard key={req.id} req={req} isGrid={viewMode === 'grid'} />)}
          </div>
        )}

        {/* Detail Popup Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
            {selectedRequest && (() => {
              const req = selectedRequest;
              const type = typeConfig[req.request_type] || typeConfig.other;
              const status = statusConfig[req.status] || statusConfig.pending;
              const urgency = urgencyConfig[req.urgency] || urgencyConfig.normal;
              const TypeIcon = type.icon;
              const StatusIcon = status.icon;
              
              return (
                <>
                  <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${type.bg} ${type.text} border ${type.border}`}><TypeIcon size={12} className="mr-1" />{type.label}</Badge>
                      <Badge className={`${status.bg} ${status.text} border ${status.border}`}><StatusIcon size={12} className="mr-1" />{status.label}</Badge>
                      <span className={`text-xs font-semibold flex items-center gap-1.5 ${urgency.color}`}>
                        {urgency.pulse && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span></span>}
                        {!urgency.pulse && <span className={`w-1.5 h-1.5 rounded-full ${urgency.dot}`} />}
                        {urgency.label}
                      </span>
                    </div>
                    <DialogTitle className="text-xl">{req.title}</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-5 py-4">
                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">From (Requester)</p>
                        <p className="text-sm font-medium flex items-center gap-1.5"><User size={14} className="text-pink-400" />{req.user_name}</p>
                      </div>
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">To (PIC)</p>
                        <p className="text-sm font-medium flex items-center gap-1.5"><User size={14} className="text-blue-400" />{(req.assigned_to_list || [req.assigned_to]).filter(Boolean).length > 0 ? (req.assigned_to_list || [req.assigned_to]).filter(Boolean).map(id => getMemberName(id)).join(', ') : <span className="text-[hsl(var(--text-muted))] italic">Not assigned</span>}</p>
                      </div>
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Created</p>
                        <p className="text-sm">{new Date(req.created_at).toLocaleString('en-MY', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3">
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Due Date</p>
                        <p className="text-sm">{req.due_date || <span className="text-[hsl(var(--text-muted))] italic">None</span>}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Description</p>
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap break-words">{req.description}</div>
                    </div>

                    {req.channel && (
                      <div className="flex items-center gap-2 text-sm">
                        <Layout size={14} className="text-cyan-400" /> <span className="text-[hsl(var(--text-muted))]">Channel:</span> <span className="font-medium">{getChannelName(req.channel)}</span>
                      </div>
                    )}

                    {/* Reference Links */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Link Reference</p>
                      {(req.reference_links?.length > 0 || req.reference_link) ? (
                        <div className="space-y-1.5">
                          {(req.reference_links?.length > 0 ? req.reference_links : [req.reference_link]).filter(Boolean).map((link, i) => (
                            <a key={link || i} href={link} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm text-cyan-400 hover:text-cyan-300 p-2 bg-[hsl(var(--surface))] rounded-lg hover:bg-cyan-500/5 transition-colors w-full min-w-0">
                              <ExternalLink size={14} className="flex-shrink-0 mt-0.5" /> <span className="min-w-0 flex-1 break-all">{link}</span>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[hsl(var(--text-muted))] italic">No reference links</p>
                      )}
                    </div>

                    {/* Remarks */}
                    {req.remarks && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">Remarks</p>
                        <p className="text-sm text-white whitespace-pre-line bg-[hsl(var(--surface))] rounded-lg p-3">{req.remarks}</p>
                      </div>
                    )}

                    {/* Per-unit tracking — quantity > 1 means each unit can have its own PIC & Done button */}
                    {Array.isArray(req.sub_units) && req.sub_units.length > 0 && (
                      <div className="border-t border-[hsl(var(--border))] pt-4">
                        <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-1">
                          <Layout size={12} /> Units ({req.sub_units.filter(u => u.status === 'completed').length}/{req.sub_units.length} done)
                        </p>
                        <div className="space-y-2">
                          {req.sub_units.map((u) => {
                            const done = u.status === 'completed';
                            const myUnit = u.assigned_to === currentUser.id;
                            // Any PIC listed on the parent request can collaborate
                            // on sub-units (matches backend permission logic).
                            const isRequestPic = Array.isArray(req.assigned_to_list)
                              ? req.assigned_to_list.includes(currentUser.id)
                              : (req.assigned_to === currentUser.id);
                            // STRICT: only the PIC of THIS unit (or admin) can press Done.
                            // Request owner can no longer interfere — prevents accidental scorecard tampering.
                            const canMark = myUnit || isAdmin;
                            // PIC reassignment: admin, request owner, current unit PIC, or any request-level PIC.
                            const canReassign = (isAdmin || req.user_id === currentUser.id || myUnit || isRequestPic) && !done;
                            // Label rename: admin, request owner, or any request-level PIC (incl. current unit PIC).
                            const canRename = isAdmin || req.user_id === currentUser.id || myUnit || isRequestPic;
                            return (
                              <div key={u.idx} className={`p-2.5 rounded-lg border ${done ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]'}`} data-testid={`sub-unit-${u.idx}`}>
                                {/* Title row — editable inline by PIC/owner/admin */}
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`w-7 h-7 flex-shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${done ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))]'}`}>#{u.idx}</span>
                                  {canRename ? (
                                    <Input
                                      defaultValue={u.label || ''}
                                      placeholder={`Unit #${u.idx} title (optional)`}
                                      onBlur={(e) => {
                                        const v = (e.target.value || '').trim();
                                        if (v !== (u.label || '').trim()) updateSubUnit(req.id, u.idx, { label: v });
                                      }}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      className="h-7 flex-1 bg-transparent border-0 border-b border-dashed border-[hsl(var(--border))] focus:border-cyan-400 rounded-none text-xs px-1 font-medium"
                                      data-testid={`unit-label-edit-${u.idx}`}
                                    />
                                  ) : (
                                    <span className="text-xs font-medium text-white flex-1" data-testid={`unit-label-view-${u.idx}`}>
                                      {u.label || <span className="text-[hsl(var(--text-muted))] italic">Unit #{u.idx}</span>}
                                    </span>
                                  )}
                                </div>
                                {/* PIC + Done row */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pl-9">
                                  <div className="flex-1 min-w-0">
                                    {canReassign ? (
                                      <Select value={u.assigned_to || ''} onValueChange={(v) => updateSubUnit(req.id, u.idx, { assigned_to: v })}>
                                        <SelectTrigger className="h-8 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-xs" data-testid={`unit-assign-${u.idx}`}><SelectValue placeholder="Pick PIC" /></SelectTrigger>
                                        <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                          {picMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.department ? ` (${m.department})` : ''}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <span className="text-xs text-blue-400 flex items-center gap-1.5"><User size={12} />{u.assigned_to ? getMemberName(u.assigned_to) : 'Unassigned'}</span>
                                    )}
                                    {done && u.completed_by_name && (
                                      <p className="text-[10px] text-emerald-400 mt-0.5">Done by <span className="font-medium">{u.completed_by_name}</span>{u.completed_at && ` • ${new Date(u.completed_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}`}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    {!done ? (
                                      <Button
                                        size="sm"
                                        disabled={!canMark}
                                        onClick={() => updateSubUnit(req.id, u.idx, { status: 'completed' })}
                                        className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-xs disabled:opacity-30 disabled:cursor-not-allowed disabled:bg-[hsl(var(--surface-elevated))]"
                                        title={canMark ? 'Mark this unit done' : 'Only the assigned PIC (or admin) can mark Done'}
                                        data-testid={`unit-done-${u.idx}`}
                                      >
                                        <CheckCircle2 size={12} className="mr-1" /> Done
                                      </Button>
                                    ) : isAdmin ? (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateSubUnit(req.id, u.idx, { status: 'not_started' })}
                                        className="h-8 px-2 text-xs text-amber-400 hover:bg-amber-500/10"
                                        data-testid={`unit-revoke-${u.idx}`}
                                      >
                                        Undo
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Completed Links */}
                    <div className="border-t border-[hsl(var(--border))] pt-4">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-1"><CheckCircle2 size={12} /> Link Completed</p>
                      {(req.completed_links || []).length > 0 ? (
                        <div className="space-y-1.5 mb-3">
                          {(req.completed_links || []).map((link, i) => (
                            <div key={`completed-${i}`} className="flex items-start gap-2 p-2 bg-emerald-500/5 border border-emerald-500/15 rounded-lg min-w-0">
                              {link.includes('/api/uploads/') ? <FileIcon size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" /> : <ExternalLink size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />}
                              <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-400 hover:text-emerald-300 break-all flex-1 min-w-0">{link.includes('/api/uploads/') ? `Uploaded file ${i + 1}` : link}</a>
                              {canEdit(req) && <button onClick={() => handleRemoveCompletedLink(req.id, i)} className="text-red-400/60 hover:text-red-400 flex-shrink-0"><X size={14} /></button>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[hsl(var(--text-muted))] italic mb-3">No completed links yet</p>
                      )}
                      {canEdit(req) && (
                        <div className="flex gap-2">
                          <Input value={newCompletedLink} onChange={(e) => setNewCompletedLink(e.target.value)} placeholder="Add completed link (drive, figma, etc)..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-sm h-9 flex-1" onKeyDown={(e) => e.key === 'Enter' && handleAddCompletedLink(req.id)} data-testid="completed-link-input" />
                          <Button size="sm" onClick={() => handleAddCompletedLink(req.id)} className="h-9 bg-emerald-600 text-white"><Plus size={14} /></Button>
                          <input type="file" ref={completedFileRef} className="hidden" accept="image/*,.pdf,.zip,.doc,.docx,.psd,.ai,.sketch,.fig" onChange={(e) => { if (e.target.files[0]) handleCompletedFileUpload(req.id, e.target.files[0]); e.target.value = ''; }} />
                          <Button size="sm" variant="outline" onClick={() => completedFileRef.current?.click()} disabled={uploadingFile} className="h-9 border-emerald-500/30 text-emerald-400">
                            {uploadingFile ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Admin Notes */}
                    {req.admin_notes && (
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3 border-l-2 border-pink-500/50">
                        <p className="text-xs text-pink-400 font-medium mb-1">Admin Notes</p>
                        <p className="text-sm text-[hsl(var(--text-secondary))]">{req.admin_notes}</p>
                      </div>
                    )}

                    {/* Comments Section */}
                    <div className="border-t border-[hsl(var(--border))] pt-4">
                      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-3 flex items-center gap-1"><MessageCircle size={12} /> Comments ({(req.comments || []).length})</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                        {(req.comments || []).length === 0 && <p className="text-sm text-[hsl(var(--text-muted))] italic">No comments</p>}
                        {(req.comments || []).map((comment) => (
                          <div key={comment.id} className="flex gap-3 p-3 bg-[hsl(var(--surface))] rounded-lg group">
                            {comment.user_avatar ? (
                              <img src={comment.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">{comment.user_name?.charAt(0) || '?'}</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium">{comment.user_name}</span>
                                <span className="text-[10px] text-[hsl(var(--text-muted))]">{new Date(comment.created_at).toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                {(comment.user_id === currentUser.id || isAdmin) && (
                                  <button onClick={() => handleDeleteComment(req.id, comment.id)} className="ml-auto opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-all"><Trash2 size={12} /></button>
                                )}
                              </div>
                              <p className="text-sm text-[hsl(var(--text-secondary))] break-words">{comment.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <MentionInput
                          value={newComments[req.id] || ''}
                          onChange={(val) => setNewComments({ ...newComments, [req.id]: val })}
                          onSubmit={() => handleAddComment(req.id)}
                          submitting={submittingComment === req.id}
                          placeholder="Write a comment... (type @ to mention)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  {canEdit(req) && (
                    <DialogFooter className="border-t border-[hsl(var(--border))] pt-4 flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openEditModal(req)} className="border-amber-500/30 text-amber-400"><Edit2 size={14} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(req.id, 'in_progress')} className="border-blue-500/30 text-blue-400"><Clock size={14} className="mr-1" /> In Progress</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(req.id, 'completed')} className="border-emerald-500/30 text-emerald-400"><CheckCircle2 size={14} className="mr-1" /> Complete</Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(req.id, 'amendment')} className="border-orange-500/30 text-orange-400"><Edit2 size={14} className="mr-1" /> Amendment</Button>
                      {isAdmin && <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(req.id, 'rejected')} className="border-red-500/30 text-red-400"><XCircle size={14} className="mr-1" /> Reject</Button>}
                      {(req.user_id === currentUser.id || isAdmin) && <Button size="sm" variant="outline" onClick={() => { if (window.confirm('Delete this request?')) { handleDelete(req.id); } }} className="border-red-500/30 text-red-400" data-testid="detail-delete-btn"><Trash2 size={14} className="mr-1" /> Delete</Button>}
                    </DialogFooter>
                  )}
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Add Request Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Palette size={18} className="text-pink-400" /> New Creative Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Title *</label>
                <Input data-testid="creative-request-title-input" value={newRequest.title} onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })} placeholder="e.g. Banner Promo Q1" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description *</label>
                <textarea data-testid="creative-request-desc-input" value={newRequest.description} onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })} placeholder="Describe what you need..." rows={3} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm text-[hsl(var(--text-primary))] resize-none focus:outline-none focus:ring-2 focus:ring-pink-500/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Material Type</label>
                  <Select value={newRequest.request_type} onValueChange={(v) => setNewRequest({ ...newRequest, request_type: v, material_other: v !== 'other' ? '' : newRequest.material_other })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="material-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="poster">Poster</SelectItem>
                      <SelectItem value="video_ads">Video (Ads)</SelectItem>
                      <SelectItem value="video_organic">Video (Organic)</SelectItem>
                      <SelectItem value="banner">Banner</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="other">Others</SelectItem>
                    </SelectContent>
                  </Select>
                  {newRequest.request_type === 'other' && (
                    <Input value={newRequest.material_other} onChange={(e) => setNewRequest({ ...newRequest, material_other: e.target.value })} placeholder="Enter material type..." className="mt-2 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="material-other-input" />
                  )}
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Urgency</label>
                  <Select value={newRequest.urgency} onValueChange={(v) => setNewRequest({ ...newRequest, urgency: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Quantity (units to produce)</label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={newRequest.quantity}
                  onChange={(e) => {
                    const q = Math.max(1, parseInt(e.target.value || '1', 10));
                    // Keep label array sized to quantity (preserve existing labels when shrinking/growing)
                    const labels = Array.from({ length: q }).map((_, i) => newRequest.sub_unit_labels?.[i] || '');
                    setNewRequest({ ...newRequest, quantity: q, sub_unit_labels: labels });
                  }}
                  placeholder="e.g. 5"
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"
                  data-testid="quantity-input"
                />
                {newRequest.quantity > 1 && (
                  <>
                    <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1.5">
                      Each of the {newRequest.quantity} units can be assigned to a different creative member.
                      The person who clicks <span className="text-emerald-400 font-medium">Done</span> on each unit
                      gets the scorecard credit.
                    </p>
                    {/* Optional per-unit titles. Both user and creative can rename later in the detail dialog. */}
                    <div className="mt-3 p-3 rounded-lg bg-[hsl(var(--surface))] border border-dashed border-[hsl(var(--border))]">
                      <p className="text-[11px] uppercase tracking-wider text-cyan-400 mb-2 font-medium">Unit Titles (optional)</p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))] mb-2.5">
                        Name each unit so everyone knows which one is which (e.g. <span className="italic">intro, qna, feedback</span>). Leave blank if not needed — PIC can rename later.
                      </p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {Array.from({ length: newRequest.quantity }).map((_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-7 h-7 flex-shrink-0 rounded-md bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-muted))] flex items-center justify-center text-[11px] font-bold">#{i + 1}</span>
                            <Input
                              value={newRequest.sub_unit_labels?.[i] || ''}
                              onChange={(e) => {
                                const labels = [...(newRequest.sub_unit_labels || [])];
                                while (labels.length < newRequest.quantity) labels.push('');
                                labels[i] = e.target.value;
                                setNewRequest({ ...newRequest, sub_unit_labels: labels });
                              }}
                              placeholder={`e.g. ${['intro', 'qna', 'feedback', 'outro', 'highlight'][i % 5]}`}
                              className="h-8 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-xs"
                              data-testid={`unit-label-input-${i + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Channel <span className="text-red-400">*</span></label>
                  <Select value={newRequest.channel} onValueChange={(v) => setNewRequest({ ...newRequest, channel: v, channel_other: v === 'others' ? newRequest.channel_other : '' })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="channel-select"><SelectValue placeholder="Select channel" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                      <SelectItem value="sembang-ohh-yeahh">Sembang Ohh Yeahh</SelectItem>
                      <SelectItem value="tenderflix">Tenderflix</SelectItem>
                      <SelectItem value="lotifys">Lotifys</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                  {newRequest.channel === 'others' && (
                    <Input value={newRequest.channel_other} onChange={(e) => setNewRequest({ ...newRequest, channel_other: e.target.value })} placeholder="Enter channel/project name..." className="mt-2 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                  )}
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Assign To (PIC) — Creative Dept</label>
                  <div className="space-y-2">
                    {(newRequest.assigned_to || []).map((picId, i) => (
                      <div key={`input-${i}`} className="flex gap-2">
                        <Select value={picId} onValueChange={(v) => { const pics = [...(newRequest.assigned_to || [])]; pics[i] = v; setNewRequest({ ...newRequest, assigned_to: pics }); }}>
                          <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] flex-1"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                            {picMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.department ? ` (${m.department})` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {(newRequest.assigned_to || []).length > 1 && <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400" onClick={() => { const pics = (newRequest.assigned_to || []).filter((_, j) => j !== i); setNewRequest({ ...newRequest, assigned_to: pics }); }}><X size={14} /></Button>}
                      </div>
                    ))}
                    {(newRequest.assigned_to || []).length === 0 && (
                      <Select value="" onValueChange={(v) => setNewRequest({ ...newRequest, assigned_to: [v] })}>
                        <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                          {picMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.department ? ` (${m.department})` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <Button variant="ghost" size="sm" className="text-xs text-pink-400" onClick={() => setNewRequest({ ...newRequest, assigned_to: [...(newRequest.assigned_to || []), ''] })}><Plus size={12} className="mr-1" /> Add PIC</Button>
                  </div>
                </div>
              </div>
              {/* Reference Links */}
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Reference Links</label>
                {newRequest.reference_links.map((link, i) => (
                  <div key={`field-${i}`} className="flex gap-2 mb-2">
                    <Input value={link} onChange={(e) => { const links = [...newRequest.reference_links]; links[i] = e.target.value; setNewRequest({ ...newRequest, reference_links: links }); }} placeholder="https://..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                    {newRequest.reference_links.length > 1 && <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400" onClick={() => { const links = newRequest.reference_links.filter((_, j) => j !== i); setNewRequest({ ...newRequest, reference_links: links }); }}><X size={14} /></Button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs text-pink-400" onClick={() => setNewRequest({ ...newRequest, reference_links: [...newRequest.reference_links, ''] })}><Plus size={12} className="mr-1" /> Add Link</Button>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Remarks</label>
                <Textarea value={newRequest.remarks} onChange={(e) => setNewRequest({ ...newRequest, remarks: e.target.value })} placeholder="Any additional notes..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] min-h-[60px]" data-testid="remarks-input" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Due Date</label>
                <Input type="date" value={newRequest.due_date} onChange={(e) => setNewRequest({ ...newRequest, due_date: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] [color-scheme:dark]" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button data-testid="submit-creative-request-btn" onClick={handleAddRequest} disabled={submitting} className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Request Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Edit2 size={18} className="text-amber-400" /> Edit Request</DialogTitle>
            </DialogHeader>
            {editRequest && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Title *</label>
                <Input value={editRequest.title} onChange={(e) => setEditRequest({ ...editRequest, title: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="edit-request-title" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description *</label>
                <textarea value={editRequest.description} onChange={(e) => setEditRequest({ ...editRequest, description: e.target.value })} rows={3} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm text-[hsl(var(--text-primary))] resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/30" data-testid="edit-request-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Material Type</label>
                  <Select value={editRequest.request_type} onValueChange={(v) => setEditRequest({ ...editRequest, request_type: v, material_other: v !== 'other' ? '' : (editRequest.material_other || '') })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="poster">Poster</SelectItem>
                      <SelectItem value="video_ads">Video (Ads)</SelectItem>
                      <SelectItem value="video_organic">Video (Organic)</SelectItem>
                      <SelectItem value="banner">Banner</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="other">Others</SelectItem>
                    </SelectContent>
                  </Select>
                  {editRequest.request_type === 'other' && (
                    <Input value={editRequest.material_other || ''} onChange={(e) => setEditRequest({ ...editRequest, material_other: e.target.value })} placeholder="Enter material type..." className="mt-2 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                  )}
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Urgency</label>
                  <Select value={editRequest.urgency} onValueChange={(v) => setEditRequest({ ...editRequest, urgency: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Channel</label>
                  <Select value={editRequest.channel || 'none'} onValueChange={(v) => setEditRequest({ ...editRequest, channel: v === 'none' ? '' : v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select channel" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="none">None</SelectItem>
                      {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Assign To (PIC)</label>
                  <div className="space-y-2">
                    {(editRequest.assigned_to_list || []).map((picId, i) => (
                      <div key={`input-${i}`} className="flex gap-2">
                        <Select value={picId} onValueChange={(v) => { const pics = [...(editRequest.assigned_to_list || [])]; pics[i] = v; setEditRequest({ ...editRequest, assigned_to_list: pics }); }}>
                          <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] flex-1"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                          <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                            {picMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.department ? ` (${m.department})` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {(editRequest.assigned_to_list || []).length > 1 && <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400" onClick={() => { const pics = (editRequest.assigned_to_list || []).filter((_, j) => j !== i); setEditRequest({ ...editRequest, assigned_to_list: pics }); }}><X size={14} /></Button>}
                      </div>
                    ))}
                    {(editRequest.assigned_to_list || []).length === 0 && (
                      <Select value="" onValueChange={(v) => setEditRequest({ ...editRequest, assigned_to_list: [v] })}>
                        <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                          {picMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.department ? ` (${m.department})` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <Button variant="ghost" size="sm" className="text-xs text-amber-400" onClick={() => setEditRequest({ ...editRequest, assigned_to_list: [...(editRequest.assigned_to_list || []), ''] })}><Plus size={12} className="mr-1" /> Add PIC</Button>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Reference Links</label>
                {editRequest.reference_links.map((link, i) => (
                  <div key={`field-${i}`} className="flex gap-2 mb-2">
                    <Input value={link} onChange={(e) => { const links = [...editRequest.reference_links]; links[i] = e.target.value; setEditRequest({ ...editRequest, reference_links: links }); }} placeholder="https://..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                    {editRequest.reference_links.length > 1 && <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400" onClick={() => { const links = editRequest.reference_links.filter((_, j) => j !== i); setEditRequest({ ...editRequest, reference_links: links }); }}><X size={14} /></Button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs text-amber-400" onClick={() => setEditRequest({ ...editRequest, reference_links: [...editRequest.reference_links, ''] })}><Plus size={12} className="mr-1" /> Add Link</Button>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Due Date</label>
                <Input type="date" value={editRequest.due_date} onChange={(e) => setEditRequest({ ...editRequest, due_date: e.target.value })} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] [color-scheme:dark]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Remarks</label>
                <Textarea value={editRequest.remarks || ''} onChange={(e) => setEditRequest({ ...editRequest, remarks: e.target.value })} placeholder="Any additional notes..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] min-h-[60px]" />
              </div>
            </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={handleEditRequest} disabled={editSubmitting} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="save-edit-request-btn">
                {editSubmitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
